import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { faAngleDown, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import Collapse from './Collapse';
import Icon from './Icon';
import Tooltip from './Tooltip';
import Typography from './Typography';
import * as S from './styled/Accordion.styled';

/**
 *  @author @buunsal
 *
 *  @param {Object} header - Header of the Accordion component.
 * 	@param {String} [headerTooltip] - Tooltip of the header.
 *	@param {Boolean} [defaultVisible] - If the content is visible by default.
 *	@param {Object} [style] - Style of the container.
 *	@param {String} [className] - Class name of the container.
 *	@param {Object} [contentStyle] - Style of the content.
 *	@param {Object} [headerStyle] - Style of the header.
 *	@param {String} [contentClassName] - Class name of the content.
 *	@param {Object} children - Content of the component.
 */
const Accordion = (
	{ header, headerTooltip, defaultVisible, style, className, headerStyle, contentStyle, contentClassName, children },
	ref
) => {
	const { t } = useTranslation();
	const headerRef = useRef(null);
	const [visible, setVisible] = useState();

	useImperativeHandle(
		ref,
		() => ({
			open() {
				setVisible(true);
				setTimeout(() => {
					setVisible();
				}, 0);
			},
			close() {
				setVisible(false);
				setTimeout(() => {
					setVisible();
				}, 0);
			}
		}),
		[setVisible]
	);

	return (
		<S.AccordionContainer style={style} className={`accordion ${className}`}>
			<S.AccordionHeader ref={headerRef} style={headerStyle} className="accordion-header">
				<Typography className="accordion-header-typography" variant="p2" fontWeight="500">
					{typeof header === 'string' ? t(header) : header}
					{headerTooltip && (
						<Tooltip title={headerTooltip}>
							<Icon icon={faCircleInfo} className="info-icon" />
						</Tooltip>
					)}
				</Typography>
				<Icon className="expand-icon" icon={faAngleDown} size="medium" />
			</S.AccordionHeader>

			<Collapse buttonRef={headerRef} defaultVisible={defaultVisible} visible={visible}>
				<S.AccordionContentContainer
					style={contentStyle}
					className={`accordion-content-container ${contentClassName}`}>
					{children}
				</S.AccordionContentContainer>
			</Collapse>
		</S.AccordionContainer>
	);
};

export default forwardRef(Accordion);
